import { useEffect, useRef } from 'react';

import './AuthRequiredDialog.css';

export type AuthRequiredAction = 'save' | 'interest' | 'callback';

export type AuthRequiredDialogProps = {
  open: boolean;
  title: string;
  body: string;
  signInLabel: string;
  cancelLabel: string;
  onCancel: () => void;
  onSignIn: () => void;
};

// Surfaced when an anonymous visitor clicks Save / Express Interest / Request
// Callback on `/deals/:slug` (ADR-025 §D; resume now implemented — see
// ADR-048). The cancel button is auto-focused on open (least destructive
// default; users can Esc out the same way they would for any modal in the
// app). `onSignIn` is the page's responsibility — it calls
// `navigate('/signin?redirect=<deal URL with ?action=…>')`; DealPage replays
// the action on return.
export function AuthRequiredDialog({
  open,
  title,
  body,
  signInLabel,
  cancelLabel,
  onCancel,
  onSignIn,
}: AuthRequiredDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelButtonRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="confirm-modal__backdrop" role="presentation" onClick={onCancel}>
      <div
        className="confirm-modal auth-required-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-required-title"
        aria-describedby="auth-required-body"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="auth-required-title" className="confirm-modal__title">
          {title}
        </h2>
        <p id="auth-required-body" className="confirm-modal__body">
          {body}
        </p>
        <div className="confirm-modal__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            className="confirm-modal__btn confirm-modal__btn--cancel"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onSignIn}
            className="confirm-modal__btn confirm-modal__btn--confirm"
          >
            {signInLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
